// @flow

import buildOptionsKey from 'app/common/redux/reducers/resources/buildOptionsKey';
import logStats from 'app/common/lib/logStats';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import Stopwatch from 'app/packages/trace-logger/Stopwatch';
import traceLogger from 'app/packages/trace-logger/singleton';
import type { Action, State } from 'app/common/redux/types';
import type { Dispatch, MiddlewareAPI } from 'redux';
import {
  resourceFetchCreate,
  resourceFetchComplete,
  resourceFetchMoreComplete,
} from 'app/common/redux/actions/resourceFetch';

const END_BOOKMARK = '-end-';
export const MAX_EMPTY_DATA_RETRY_COUNT = 10;

const hasMore = (nextBookmark?: string): boolean => !!nextBookmark && nextBookmark !== END_BOOKMARK;

// $FlowFixMe: New error after Flow Upgrade http://pinch.pinadmin.com/flow53upgdoc
export default (store: MiddlewareAPI) => (next: Dispatch<any>) => (action: Action): State => {
  if (
    action.type === 'RESOURCE_FETCH' ||
    action.type === 'RESOURCE_PREFETCH' ||
    action.type === 'RESOURCE_FETCH_MORE'
  ) {
    const isPrefetch = action.type === 'RESOURCE_PREFETCH';
    const isFetchMore = action.type === 'RESOURCE_FETCH_MORE';
    const {
      name,
      options = {},
      moduleRenderOptions = {},
      headers = {},
      traceLoggerHandlerName = '',
    } = action.payload;

    const callOptions = { showError: false, tracingParams: {} };
    const { resources } = store.getState();

    let fetchMoreNextBookmark: string | void;
    if (isFetchMore) {
      if (resources.fetching[name] || !resources.data[name]) {
        return next(action);
      }
      fetchMoreNextBookmark = (resources.data[name][buildOptionsKey(options)] || {}).nextBookmark;
    }

    if (resources.fetching[name] && resources.fetching[name][buildOptionsKey(options)]) {
      logStats.increment('web.redux.middleware.resource_fetch_already_fetching', 0.1, {
        name,
        action_type: action.type,
      });
    }

    const complete = (data: *, error: *, nextBookmark: *) =>
      isFetchMore
        ? resourceFetchMoreComplete(name, options, data, error, nextBookmark)
        : resourceFetchComplete(name, options, data, error, nextBookmark, isPrefetch);

    let emptyDataRetryCount = 0;
    const fetch = (bookmark: string | void) => {
      const resource = ResourceFactory.create(name, {
        ...(bookmark ? { bookmarks: [bookmark] } : {}),
        isPrefetch,
        ...options,
      });

      let tracingHandler = null;
      let resourceSubspan = null;

      if (traceLoggerHandlerName) {
        tracingHandler = traceLogger.findHandlerByName(traceLoggerHandlerName);
        if (
          tracingHandler &&
          tracingHandler.hasActiveStopwatch() &&
          tracingHandler.getIsSampled()
        ) {
          const currentStopwatch = tracingHandler.getCurrentStopwatch();
          if (currentStopwatch && resource) {
            resourceSubspan = currentStopwatch.addSubStopwatch(
              new Stopwatch({
                name: resource.getResourceUrl(name, 'get'),
                metricName: tracingHandler.metricName,
                parent: currentStopwatch,
                startTime: Date.now(),
              }),
            );

            resourceSubspan.addBinaryAnnotation({
              name: resource.name,
              value: name,
              annotation_type: 'STRING',
            });

            callOptions.tracingParams = {
              traceId: tracingHandler.getCurrentTraceId(),
              spanId: resourceSubspan.spanId,
            };
          }
        }
      }

      resource.callGet(callOptions, moduleRenderOptions, headers).then(
        (response) => {
          const nextBookmark = response.bookmarks[0];
          const { data } = response.resource_response;

          if (resourceSubspan) {
            resourceSubspan.setResult(PCONST.thrift.TracingClientTypes.PWTResult.COMPLETE);
            resourceSubspan.stopTiming();
          }

          // NOTE(jackhsu) BUG-84108: Sometimes resource returns empty data (i.e. data=[]) while
          // there is still more data in the feed (next bookmark is not "-end-").
          // This happens when the API handler has filter logic after pagination logic.
          // This is a long-standing issue in the API layer, and the only workaround now is for
          // clients to request for the next page.
          if (!isPrefetch && Array.isArray(data) && data.length === 0 && hasMore(nextBookmark)) {
            const maxRetryReached = emptyDataRetryCount >= MAX_EMPTY_DATA_RETRY_COUNT;
            logStats.increment('web.resource_empty_data_retry', 1, {
              maxRetryReached,
              name,
              retry: emptyDataRetryCount,
            });
            if (!maxRetryReached) {
              emptyDataRetryCount += 1;
              fetch(nextBookmark);
              return;
            }
          }
          next(complete(data, null, nextBookmark));
        },
        (error) => {
          if (resourceSubspan) {
            resourceSubspan.setResult(PCONST.thrift.TracingClientTypes.PWTResult.ERROR);
            resourceSubspan.stopTiming();
          }
          next(complete(null, error, undefined));
        },
      );
    };

    fetch(fetchMoreNextBookmark);

    return next(resourceFetchCreate(name, options));
  }

  return next(action);
};
